/** GCP's grant-monitor fix generators: how to remediate a finding, as
 * opposed to `./gcp.ts`'s monitors, which describe what is risky. Stays in
 * `shared` (rather than `@p0security/integrations-gcloud-backend`) because `frontend`
 * reads `PresetMonitors[...].fix.generate` directly and shouldn't depend on
 * gcloud's backend-tier package. */
import { camelCase, capitalize } from "lodash-es";

import type { ItemAssessmentScope } from "../../../../types/assessment.ts";
import type {
  AssessmentNodes,
  GrantNode,
} from "../../../../types/assessment/data/index.ts";
import type { FixOptions } from "../../../../types/assessment/monitor.ts";
import type { GcpResourceInfo } from "../../gcp-asset.ts";
import { MemberKey, Service, frnToInfo } from "../../gcp-asset.ts";

const gcpFixRewrite: Record<
  string,
  (
    info: GcpResourceInfo
  ) => (GcpResourceInfo & { component?: string }) | undefined
> = {
  [Service.kms]: (info) =>
    info.type === "keyRings" && info.id
      ? // using the full name allows us to omit the `--location` flag
        { ...info, component: "keyrings", service: "kms" }
      : undefined,
  [Service.crm]: (info) =>
    info.type && info.id
      ? { ...info, service: info.type, locator: info.id }
      : undefined,
  [Service.functions]: (info) =>
    info.type === "functions" && info.id ? info : undefined,
  [Service.iam]: (info) =>
    info.type === "serviceAccounts" && info.id
      ? { ...info, locator: info.id, component: "service-accounts" }
      : undefined,
  [Service.storage]: (info) => ({
    ...info,
    component: "buckets",
    locator: `gs://${info.locator}`,
  }),
};

/** Extracts project and principal identifier for project grants */
const gcpGrantFixData = (data: GrantNode["data"]) => {
  const { resources, principalType } = data;
  // GCP grants are always to a single resource
  const [resource] = resources;
  if (!resource) return undefined;

  const { api, id, locator, service, type } = frnToInfo(resource);
  if (!locator) return undefined;
  if (!service || !(service in gcpFixRewrite)) return undefined;
  const rewrite = gcpFixRewrite[service]({ api, id, locator, type, service });
  if (!rewrite) return;

  const memberKey = MemberKey[principalType];
  if (!memberKey) return undefined;
  return { ...rewrite, memberKey };
};

// see https://cloud.google.com/sdk/gcloud/reference/projects/add-iam-policy-binding
const argFromCondition = (condition: GrantNode["data"]["condition"]) =>
  typeof condition === "object"
    ? `^:^title=${condition.title}:expression=${condition.expression}${
        condition.description ? `:description=${condition.description}` : ""
      }`
    : "None";

export const unusedGrantFixGenerator = {
  gcloud: {
    description: "To fix, run these commands in Google Cloud Shell:",
    type: "shell" as const,
    generate: ({ data }: GrantNode) => {
      const { condition, principal, privilegeSet } = data;
      const fixData = gcpGrantFixData(data);
      if (!fixData) return undefined;
      const { component, locator, memberKey, service } = fixData;
      const conditionArg = argFromCondition(condition);
      return `gcloud ${service}${
        component ? ` ${component}` : ""
      } remove-iam-policy-binding ${locator} \\
  --member=${memberKey}:${principal} \\
  --role=${privilegeSet} \\
  --condition=${conditionArg}`;
    },

    generatePolicy: ({ data }: GrantNode) => {
      const { principal, principalType, privilegeSet, resources } = data;

      if (resources.length > 1) return undefined;
      if (principalType !== "user") return undefined;
      const resource = resources[0];

      const { id, type } = frnToInfo(resource);
      return `- name: 'Unused role for ${principal}'
  requestor:
    type: ${principalType}
    uid: ${principal}
  resource:
    type: integration
    service: gcloud
    accessType: ${type === "projects" ? "role" : "resource"}
    filters: ${
      type === "projects"
        ? `
      project:
        effect: keep
        key: id
        pattern: ^${id}$`
        : ""
    } ${
      type !== "projects"
        ? `
      resource:
        effect: keep
        key: full-resource-name
        pattern: ^${resource}$`
        : ""
    }
      role:
        effect: keep
        key: id
        pattern: ^${privilegeSet}$
  approval:
    - type: p0`;
    },
  },
};

export const partialGrantFixGenerator = {
  gcloud: {
    description:
      "Remove excess unused privileges by running these commands in Google Cloud Shell:",
    type: "shell" as const,
    generate: ({ aggregates, data }: GrantNode, { scope }: FixOptions) => {
      const { condition, principal, privilegeSet } = data;
      const fixData = gcpGrantFixData(data);
      if (!fixData) return undefined;
      const { component, locator, memberKey, service } = fixData;
      const { unknown, used } = aggregates.privileges;
      const conditionArg = argFromCondition(condition);
      const perms = [...(unknown ?? []), ...(used ?? [])].map((p) => p.key);
      // resourcemanager permissions can't be granted on a project
      // really we should use queryGrantableRoles
      const validPerms = perms.filter((p) => !p.startsWith("resourcemanager"));
      const compPart = component ? ` ${component}` : "";

      // If there are no used/unknown privileges at all, remove the role binding
      if (!perms.length) {
        return `# This command removes the role binding as there are no used or unknown privileges
gcloud ${service}${compPart} remove-iam-policy-binding ${locator} \\
  --member=${memberKey}:${principal} \\
  --role=${privilegeSet} \\
  --condition=${conditionArg}`;
      }

      // If only resourcemanager permissions are used, we can't create a custom role
      // but the role binding should remain since those permissions are still needed
      if (!validPerms.length && perms.length > 0) {
        return `# This grant only uses resourcemanager permissions, which cannot be included in custom project-level roles.
# The role binding should remain as-is since these permissions are still in use.
# To reduce privileges, consider using organization-level or folder-level roles instead.`;
      }

      const shortPrincipal = capitalize(camelCase(principal.split("@")[0]));
      const oldRole = privilegeSet.split("/").at(-1) ?? "";
      const shortOldRole = capitalize(camelCase(oldRole));
      // We have 32 characters total (64 bytes)
      const roleId = `Lp4${capitalize(
        shortPrincipal.slice(0, 16)
      )}${shortOldRole.slice(0, 12)}`;

      return `gcloud iam roles create ${roleId} \\
  --project=${scope.id} \\
  --stage="GA" \\
  --title="Least Privilege ${data.principal} ${oldRole}" \\
  --description="Least-privilege role for ${
    data.principal
  }. It replaces the previous over-privileged grant to ${
    data.privilegeSet
  }, and was determined via a P0 IAM assessment." \\
  --permissions=${validPerms.join(",")}
gcloud ${service}${compPart} add-iam-policy-binding ${locator} \\
  --member=${memberKey}:${principal} \\
  --role=projects/${scope.id}/roles/${roleId} \\
  --condition='${conditionArg}'
gcloud ${service}${compPart} remove-iam-policy-binding ${locator} \\
  --member=${memberKey}:${principal} \\
  --role=${privilegeSet} \\
  --condition='${conditionArg}'`;
    },
  },
};

const gcloudPublicAccessFix = (data: AssessmentNodes["grant"]) => {
  // IAM conditions are not allowed on public principals - we don't have to add the --condition flag to the gcloud command
  const { resources } = data;
  const [resource] = resources;
  const { id, service } = frnToInfo(resource);
  if (!id) return { error: "Grant has no resource" };
  switch (service) {
    // https://cloud.google.com/sdk/gcloud/reference/storage/buckets/add-iam-policy-binding
    case "storage":
      return {
        command: "storage buckets",
        resource: `gs://${id}`,
      };
    default:
      return {
        error: `P0 does not support public access remediation of ${service} resources.`,
      };
  }
};

export const gcpOnlyPublicAccessFixGenerator = (
  status: "disabled" | "enabled"
) => ({
  gcloud: {
    description: `${
      status === "disabled" ? "Re-enable" : "Disable"
    } public access by running these commands in Google Cloud Shell:`,
    type: "shell" as const,
    generate: (
      { data }: GrantNode,
      { scope }: { scope: ItemAssessmentScope }
    ) => {
      const { command, resource, error } = gcloudPublicAccessFix(data);
      if (error) return `# ${error}`;
      return `gcloud ${command} ${
        status === "disabled" ? "add" : "remove"
      }-iam-policy-binding '${resource}'\\
    --member='allUsers' \\
    --role='${data.privilegeSet}' \\
    --project=${scope.id}`;
    },
  },
});
